import { Button, Drawer, DrawerProps, Select } from "antd"
import {
  useExecutionSubscribableEntenteParticipants,
  useExecutionSubscribableMembers,
  useSubscribeMember,
} from "./queries"
import { useEventId } from ".."
import { object, string } from "yup"
import { Controller, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { useTranslation } from "react-i18next"
import { FormControl } from "@atoms"
import { IEvent } from "../types"

interface Props extends DrawerProps {
  onClose: () => void
  isOpen: boolean
  data?: IEvent["event_type"]
}

const SubscribeMember: React.FC<Props> = ({ data: eventType, ...props }) => {
  const { t } = useTranslation()
  const eventId = useEventId()
  const {
    formState: { errors },
    setError,
    reset,
    control,
    handleSubmit,
  } = useSubscribeMemberForm({ member_id: "" })
  const subscribableMembers = useExecutionSubscribableMembers(eventId!, {
    enabled: props.isOpen && eventType === "loisir",
  })
  const subscribableEntenteParticipants =
    useExecutionSubscribableEntenteParticipants(eventId!, {
      enabled: props.isOpen && eventType === "entente",
    })

  const { mutate, isLoading } = useSubscribeMember(eventId!, {
    meta: {
      type: "subscribe",
      entity: t("members.member", {
        ns: "operation/home",
      }),
      context: "male",
      count: 1,
    },
  })

  const onClose = () => {
    props.onClose()
    reset()
  }

  const onSubmit = (formData: ISubscribeMemberForm) => {
    mutate(formData.member_id, {
      onSuccess() {
        onClose()
      },
      onError(error: any) {
        if (error.response.data?.code === "form/validation") {
          for (let key in error.response.data.errors) {
            setError(key as any, {
              message: error.response.data.errors[key][0].message,
            })
          }
        }
      },
    })
  }

  return (
    <Drawer
      {...{ ...props, onClose }}
      title={t("members.subscription")}
      maskClosable={!isLoading}
    >
      <form>
        <FormControl>
          <Controller
            control={control}
            name="member_id"
            render={({ field }) => {
              return (
                <Select
                  loading={
                    subscribableMembers.isLoading ||
                    subscribableEntenteParticipants.isLoading
                  }
                  options={(eventType === "entente"
                    ? subscribableEntenteParticipants
                    : subscribableMembers
                  ).data?.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  showSearch
                  placeholder={t("global.select", {
                    entity: t("members.member"),
                    context: "male",
                    count: 1,
                    lettersCase: "capitalize",
                  })}
                  {...field}
                  value={field.value === "" ? undefined : field.value}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  status={errors.member_id && "error"}
                />
              )
            }}
          />
          {errors.member_id && (
            <FormControl.HelperText>
              {errors.member_id.message}
            </FormControl.HelperText>
          )}
        </FormControl>

        <Button
          loading={isLoading}
          type="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={isLoading}
        >
          {t("global.save")}
        </Button>
      </form>
    </Drawer>
  )
}

export default SubscribeMember

export interface ISubscribeMemberForm {
  member_id: string
}

export function useSubscribeMemberForm(defaultValues: ISubscribeMemberForm) {
  const schema = object().shape({
    member_id: string().required(),
  })
  return useForm({
    resolver: yupResolver(schema),
    defaultValues,
  })
}
